import {
  createDropdownOption,
  getAllNoneDropdownGroup,
} from './dropdownOptionUtils';
import {
  DropdownItem,
  type DropdownOption,
  DropdownOptionType,
} from '@ardoq/dropdown-menu';
import type { OnViewSettingsUpdate } from '@ardoq/data-model';
import { APIFieldAttributes, ViewIds } from '@ardoq/api-types';
import { CommonDropdownOptions } from '@ardoq/global-consts';
import { getRedactedFieldDisplay } from '@ardoq/renderers';
import type { I18nLocale } from '@ardoq/i18n';

const getOnClick =
  (
    viewId: string,
    key: string,
    value: string,
    wasActive: boolean,
    onViewSettingsUpdate: OnViewSettingsUpdate
  ) =>
  () => {
    const settings = { [key]: wasActive ? '' : value };
    const persistent = true;

    onViewSettingsUpdate(viewId, settings, persistent);
  };

type GetFieldDropdownOptionsArgs = {
  viewId: ViewIds;
  list: (Pick<APIFieldAttributes, 'label' | 'name'> & {
    onClick?: VoidFunction;
    isConfidential?: boolean;
  })[];
  settingsKey: string;
  isActive: (fieldName: string) => boolean;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  truncateLabel?: boolean;
};

export const getFieldDropdownOptions = ({
  viewId,
  list,
  settingsKey,
  isActive,
  onViewSettingsUpdate,
  truncateLabel = true,
}: GetFieldDropdownOptionsArgs): DropdownOption[] =>
  list.map<DropdownOption>(field => {
    const active = isActive(field.name);
    const isConfidential = !!field.isConfidential;
    return {
      name: field.name,
      label: field.label,
      isActive: active,
      isDisabled: isConfidential,
      rightContent: isConfidential ? getRedactedFieldDisplay() : undefined,
      onClick:
        field.onClick ||
        getOnClick(
          viewId,
          settingsKey,
          field.name,
          active,
          onViewSettingsUpdate
        ),
      type: DropdownOptionType.OPTION,
      truncateLabel,
    };
  });

export const getFieldMultiSelectDropdownOptions = (
  viewId: ViewIds,
  availableOptions: (Pick<APIFieldAttributes, 'label' | 'name'> & {
    deselectedByDefault?: boolean;
  })[],
  viewState: Record<string, string[]>,
  settingsKey: string,
  onViewSettingsUpdate: OnViewSettingsUpdate,
  i18nLocale: I18nLocale
): DropdownItem[] => [
  ...getAllNoneDropdownGroup({
    viewId,
    viewState,
    key: settingsKey,
    availableOptions: availableOptions.map(field => field.name),
    onViewSettingsUpdate,
    i18nLocale,
  }),
  ...availableOptions.map(field =>
    createDropdownOption({
      name: field.name,
      label: field.label,
      value: field.name,
      viewId,
      viewState,
      key: settingsKey,
      availableOptions: viewState[settingsKey].includes(
        CommonDropdownOptions.ALL_EXCEPT_DESELECTED_BY_DEFAULT
      )
        ? availableOptions
            .filter(field => !field.deselectedByDefault)
            .map(field => field.name)
        : availableOptions.map(field => field.name),
      deselectedByDefault: field.deselectedByDefault,
      onViewSettingsUpdate,
    })
  ),
];
